import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Crown, Skull } from "lucide-react";
import GuildLogo from "@/components/GuildLogo";

/**
 * Guild Wars panel — visible in the Guilds view when the caller is in a
 * guild. Shows the caller's active wars this ISO-week (with tally +
 * winning side highlighted) and lets the leader declare a new war
 * against any other guild.
 */
export default function GuildWars({ ch, guilds, myGuild, isLeader }) {
  const [wars, setWars] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);

  const load = async () => {
    try {
      const res = await api.guildWarActive();
      setWars(res.wars || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const declare = async (gid) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.guildWarDeclare(gid);
      toast.success("War declared!");
      setPickOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "War declaration failed");
    } finally { setBusy(false); }
  };

  if (!myGuild) return null;
  const targets = (guilds || []).filter(g => g.guild_id !== myGuild.guild_id);

  return (
    <div className="panel p-3" data-testid="guild-wars">
      <div className="flex items-center gap-2 mb-2">
        <Swords className="w-3 h-3 text-[#8A0303]" strokeWidth={1.5} />
        <span className="font-heading text-[10px] tracking-widest uppercase text-[#8A0303]">
          Guild Wars · This Week
        </span>
        {isLeader && (
          <button
            data-testid="guild-war-declare-open"
            disabled={busy}
            onClick={() => setPickOpen(v => !v)}
            className="ml-auto px-2 py-1 border border-[#8A0303] text-[#E33333] font-heading text-[9px] tracking-widest uppercase disabled:opacity-30"
          >
            Declare
          </button>
        )}
      </div>

      {pickOpen && (
        <div className="mb-3 max-h-40 overflow-y-auto space-y-1 border-t border-[#262626] pt-2" data-testid="war-target-list">
          {targets.length === 0 && (
            <div className="text-[10px] italic text-[#666]">No rival guilds exist.</div>
          )}
          {targets.map(g => (
            <button
              key={g.guild_id}
              data-testid={`war-target-${g.guild_id}`}
              disabled={busy}
              onClick={() => declare(g.guild_id)}
              className="w-full flex items-center gap-2 p-1.5 border border-[#333] hover:border-[#8A0303] transition-colors text-left"
            >
              <GuildLogo logo={g.logo || "raven"} size={20} color={g.accent_color || "#8A0303"} />
              <span className="font-heading text-[10px] tracking-widest uppercase text-[#E2DFD8]">
                [{g.tag}] {g.name}
              </span>
            </button>
          ))}
        </div>
      )}

      {wars.length === 0 && (
        <div className="text-center text-[10px] italic text-[#666] py-2">
          No active wars this week.
        </div>
      )}

      {wars.map(w => {
        const myKills = w.kills[myGuild.guild_id] || 0;
        const otherId = w.pair.find(p => p !== myGuild.guild_id);
        const otherKills = w.kills[otherId] || 0;
        const otherName = w.declaring === myGuild.guild_id ? w.name_b : w.name_a;
        const otherTag = w.declaring === myGuild.guild_id ? w.tag_b : w.tag_a;
        const winning = myKills > otherKills;
        const tied = myKills === otherKills;
        return (
          <div
            key={w.war_id}
            className="border p-2 space-y-1 mb-1"
            style={{ borderColor: winning ? "#6E8B3D" : tied ? "#B89C3A" : "#8A0303" }}
            data-testid={`war-${w.war_id}`}
          >
            <div className="flex items-center gap-2">
              <Crown className={`w-3 h-3 ${winning ? "text-[#6E8B3D]" : "text-[#8A0303]"}`} strokeWidth={1.5} />
              <span className="font-heading text-[10px] tracking-widest uppercase text-[#E2DFD8]">
                [{myGuild.tag}] vs [{otherTag}] {otherName}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono">
              <div className="flex-1">
                <div className="flex justify-between text-[10px] uppercase tracking-widest">
                  <span className="text-[#E5C34A]">{myGuild.tag}</span>
                  <span style={{ color: winning ? "#6E8B3D" : "#B0ADA5" }}>{myKills} kills</span>
                </div>
                <div className="vbar mt-1 h-1.5">
                  <span style={{
                    width: `${myKills + otherKills > 0 ? (myKills / (myKills + otherKills)) * 100 : 50}%`,
                    background: winning ? "#6E8B3D" : "#B0ADA5",
                  }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono">
              <div className="flex-1">
                <div className="flex justify-between text-[10px] uppercase tracking-widest">
                  <span className="text-[#B0ADA5]">{otherTag}</span>
                  <span style={{ color: !winning && !tied ? "#E33333" : "#B0ADA5" }}>{otherKills} kills</span>
                </div>
                <div className="vbar mt-1 h-1.5">
                  <span style={{
                    width: `${myKills + otherKills > 0 ? (otherKills / (myKills + otherKills)) * 100 : 50}%`,
                    background: !winning && !tied ? "#8A0303" : "#8c8c8c",
                  }} />
                </div>
              </div>
            </div>
            <div className="font-mono text-[9px] text-[#666] tracking-widest uppercase flex items-center gap-1">
              <Skull className="w-3 h-3" strokeWidth={1.5} />
              Duel a member of [{otherTag}] to score a kill
            </div>
          </div>
        );
      })}
    </div>
  );
}
